import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import {
  ANALYSIS_TERMINAL_STATUSES,
  AnalysisStatus,
  type WorkspaceType,
} from '@/features/workspaces/constants';
import type {
  ContractRedline,
  ContractReviewSource,
  DeviationSource,
} from '@/features/standard-deviations/types';
import { humanizeDomainName } from '@/features/contract-review/utils';
import { parseStreamRow, queryStream } from '@/features/workspaces/stream';
import { resourceLatestVersionQuery } from '@/features/workspaces/queries';
import type { VersionStatus } from '@/features/workspaces/types';

export const CONTRACT_REVIEW_QUERY_KEY = 'contractReview' as const;
export const CONTRACT_REVIEW_DEVIATIONS_QUERY_KEY = 'contractReviewDeviations' as const;

export const contractReviewQuery = (type: WorkspaceType, resourceId: string) => {
  const params = { type: DOCUMENT_TYPE[type], resourceId };
  const queryKey = [CONTRACT_REVIEW_QUERY_KEY, type, resourceId] as const;
  return queryOptions({
    queryKey,
    queryFn: async () => {
      const { items, versionStatus } = (
        await api.get<{
          versionStatus: VersionStatus;
          items: {
            id: string;
            status: AnalysisStatus;
            progress: number;
            logicalAnalysisId: string | null;
            isCurrentRun: boolean;
            isPublishedRun: boolean;
          }[];
        }>('v1/analysis/legal_deviations', { params: { ...params, includeStatements: false } })
      ).data;
      const latest = items.find((item) => item.isCurrentRun);
      const run =
        items.find(
          (item) => item.isPublishedRun && item.logicalAnalysisId === latest?.logicalAnalysisId,
        ) ??
        items.find((item) => item.isPublishedRun) ??
        latest;
      return {
        versionStatus,
        latestRunId: latest?.id,
        status: latest?.status,
        progress: latest?.progress,
        review: run
          ? (
              await api.get<{
                id: string;
                status: AnalysisStatus;
                standardEvalId: string;
                createdAt: string;
                redFlagTemplateId: string | null;
                redFlagTemplateName: string | null;
                domainSummary: {
                  domain: string;
                  domainDisplayName: string | null;
                  domainNarrative: string | null;
                  riskScore: number;
                  domainRiskScore: number | null;
                  criticalDeviationCount: number;
                  totalDeviations: number;
                }[];
                clusterDetails: Record<
                  string,
                  {
                    id: string;
                    displayNumber?: number;
                    clusterName: string | null;
                    clusterSummary: string | null;
                    consolidatedRiskDescription: string | null;
                    representative: { statement: string };
                    userReview: string | null;
                    reviewRevision?: number;
                    evidenceRedFlag: string | null;
                    evidenceNeedsReview: string | null;
                    evidenceMinorIssues: string | null;
                    evidenceSources: {
                      refKey: string;
                      type: 'deviation' | 'clause_scanner';
                      id: string;
                    }[];
                  }[]
                >;
                compoundRisk: {
                  narrative: string;
                  reviewResult: {
                    template_assessments: {
                      definition_id: string;
                      domain_key: string;
                      verdict:
                        | 'adverse_trigger'
                        | 'potential_trigger'
                        | 'no_adverse_trigger_found'
                        | 'unresolved'
                        | 'not_applicable';
                      explanation: string;
                    }[];
                    clarifications: {
                      key: string;
                      definition_ids: string[];
                      question: string;
                      consequence: string;
                      sources: ContractReviewSource[];
                    }[];
                    summary_sources?: ContractReviewSource[];
                  } | null;
                } | null;
              }>(`v1/analysis/legal_deviations/${run.id}/clustering`, { params })
            ).data
          : null,
      };
    },
    meta: {
      stream: queryStream<
        { status?: AnalysisStatus },
        | {
            rows: {
              id: string;
              status: AnalysisStatus;
              progress: number;
              is_current_run: boolean;
            }[];
          }
        | { id?: string | null; status: AnalysisStatus | null; progress?: number | null }
      >({
        topic: 'legal_deviation',
        type: params.type,
        resourceId,
        versioned: true,
        isActive: (data) =>
          data?.status != null && !ANALYSIS_TERMINAL_STATUSES.includes(data.status),
        onStatusUpdate: (payload, queryClient) => {
          const invalidate = () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries(resourceLatestVersionQuery(resourceId));
          };
          const current = queryClient.getQueryData<{
            latestRunId?: string;
            status?: AnalysisStatus;
            progress?: number;
          }>(queryKey);
          if ('rows' in payload) {
            const latest = payload.rows.find((row) => row.is_current_run);
            if (
              latest?.id === current?.latestRunId &&
              latest?.status === current?.status &&
              latest?.progress === current?.progress
            )
              return;
            return invalidate();
          }
          const row = parseStreamRow(payload);
          if (row === 'refetch') return invalidate();
          if (!row) return;
          if (row.rowId !== current?.latestRunId || ANALYSIS_TERMINAL_STATUSES.includes(row.status))
            return invalidate();
          queryClient.setQueryData<{ status?: AnalysisStatus; progress?: number }>(
            queryKey,
            (old) => old && { ...old, status: row.status, progress: row.progress },
          );
        },
      }),
    },
    select: ({ versionStatus, status, progress, review }) => ({
      versionStatus,
      status,
      progress,
      review,
      hasResult:
        review?.status === AnalysisStatus.DONE || review?.status === AnalysisStatus.NOT_FOUND,
      isRunning: status === AnalysisStatus.IN_PROGRESS || status === AnalysisStatus.NOT_STARTED,
      domains: (review?.domainSummary ?? [])
        .map((domain) => {
          const templateAssessments =
            review?.compoundRisk?.reviewResult?.template_assessments.filter(
              (assessment) => assessment.domain_key === domain.domain,
            ) ?? [];
          return {
            ...domain,
            name: humanizeDomainName(domain.domainDisplayName ?? domain.domain),
            riskScore: domain.domainRiskScore ?? domain.riskScore,
            clusters: review?.clusterDetails[domain.domain] ?? [],
            templateAssessments,
            clarifications:
              review?.compoundRisk?.reviewResult?.clarifications.filter(
                (clarification) =>
                  !clarification.definition_ids.length ||
                  templateAssessments.some((assessment) =>
                    clarification.definition_ids.includes(assessment.definition_id),
                  ),
              ) ?? [],
          };
        })
        .sort(
          (a, b) =>
            b.riskScore - a.riskScore ||
            b.criticalDeviationCount - a.criticalDeviationCount ||
            b.totalDeviations - a.totalDeviations,
        ),
    }),
  });
};

export const contractReviewDeviationsQuery = (
  type: WorkspaceType,
  resourceId: string,
  reviewId: string,
) =>
  queryOptions({
    queryKey: [CONTRACT_REVIEW_DEVIATIONS_QUERY_KEY, type, resourceId, reviewId],
    queryFn: async () =>
      (
        await api.get<{
          deviationsByCluster: Record<
            string,
            {
              id: string;
              statement: string;
              analysis: string | null;
              severityRationale: string | null;
              riskShiftDescription: string | null;
              redline: ContractRedline | null;
              userReview: string | null;
              reviewRevision?: number;
              sources: DeviationSource[];
            }[]
          >;
        }>(`v1/analysis/legal_deviations/${reviewId}/clusters/deviations`, {
          params: { type: DOCUMENT_TYPE[type], resourceId },
        })
      ).data.deviationsByCluster,
  });
